import React, { useState } from 'react';
import PropTypes from 'prop-types';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import { Box, IconButton, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@material-ui/core';
import { Button, Chip } from '@orijinworks/frontend-commons';
import { AttributeModal } from './AttributeModal';
import { ServiceModal } from '../sections/services/ServiceModal';
import { getPersonDisplayName } from '../persons-manager.helpers';
import { usePersonDetailViewStyles } from './person-detail-view.style';

const statePropType = PropTypes.shape({
  items: PropTypes.arrayOf(PropTypes.shape({})),
  status: PropTypes.oneOf(['loading', 'idle', 'ok', 'error']),
});

const personPropType = PropTypes.shape({
  displayFirstName: PropTypes.string,
  displayLastName: PropTypes.string,
  docId: PropTypes.string,
  email: PropTypes.string,
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  personId: PropTypes.number.isRequired,
  username: PropTypes.string,
});

const profileHrefForPerson = (personId) => `/#/users/${personId}`;

export function PersonDetailView({
  attributesState,
  onBack,
  onSaved,
  person,
  servicesState,
}) {
  const classes = usePersonDetailViewStyles();
  const [attributesOpen, setAttributesOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);

  const attributes = attributesState?.items || [];
  const services = servicesState?.items || [];

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Box>
          <Typography variant="h6">{getPersonDisplayName(person)}</Typography>
          <Typography variant="body2">
            @{person.username || '—'} · Doc ID: {person.docId || '—'}
          </Typography>
          <Typography variant="body2">{person.email || '—'}</Typography>
        </Box>
        <Box>
          <Button variant="outlined" size="small" onClick={onBack}>
            Back to results
          </Button>
          <Tooltip title="Open user profile in new tab">
            <IconButton
              component="a"
              href={profileHrefForPerson(person.personId)}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Open user profile in new tab"
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box className={classes.section}>
        <Box className={classes.sectionHeader}>
          <Typography variant="subtitle1">Attributes</Typography>
          <Button variant="outlined" size="small" color="primary" onClick={() => setAttributesOpen(true)}>
            Edit attributes
          </Button>
        </Box>
        {attributesState?.status === 'loading' && <Typography className={classes.sectionEmpty}>Loading attributes…</Typography>}
        {attributesState?.status === 'error' && <Typography color="error">Could not load attributes.</Typography>}
        {attributesState?.status === 'ok' && attributes.length === 0 && (
          <Typography className={classes.sectionEmpty}>No person attributes.</Typography>
        )}
        {attributesState?.status === 'ok' && attributes.length > 0 && (
          <Box className={classes.rowChips}>
            {attributes.map((row) => (
              <Chip key={row.id} size="small" label={`${row.attributeName}: ${row.value || ''}`} />
            ))}
          </Box>
        )}
      </Box>

      <Box className={classes.section}>
        <Box className={classes.sectionHeader}>
          <Typography variant="subtitle1">Services</Typography>
          <Button variant="outlined" size="small" color="primary" onClick={() => setServicesOpen(true)}>
            Edit services
          </Button>
        </Box>
        {servicesState?.status === 'loading' && <Typography className={classes.sectionEmpty}>Loading services…</Typography>}
        {servicesState?.status === 'error' && <Typography color="error">Could not load services.</Typography>}
        {servicesState?.status === 'ok' && services.length === 0 && (
          <Typography className={classes.sectionEmpty}>No person services.</Typography>
        )}
        {servicesState?.status === 'ok' && services.length > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Service</TableCell>
                <TableCell>Ext Username</TableCell>
                <TableCell>External ID</TableCell>
                <TableCell>Active</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {services.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{row.displayName || row.serviceName}</TableCell>
                  <TableCell>{row.extUsername || '—'}</TableCell>
                  <TableCell>{row.externalId || '—'}</TableCell>
                  <TableCell>{row.active ? 'Active' : 'Inactive'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Box>

      <AttributeModal
        open={attributesOpen}
        person={person}
        onClose={() => setAttributesOpen(false)}
        onSaved={onSaved}
      />
      <ServiceModal open={servicesOpen} person={person} onClose={() => setServicesOpen(false)} onSaved={onSaved} />
    </Box>
  );
}

PersonDetailView.propTypes = {
  attributesState: statePropType,
  onBack: PropTypes.func.isRequired,
  onSaved: PropTypes.func,
  person: personPropType.isRequired,
  servicesState: statePropType,
};

PersonDetailView.defaultProps = {
  attributesState: undefined,
  onSaved: undefined,
  servicesState: undefined,
};
